import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  Button, 
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Plus, Trash2 } from 'lucide-react';
import { formatMoney } from '@/lib/format';
import type { ContractFormValues } from '../../contract-form';
import type { ContractServiceConfigOption } from '@/api/contracts';
import type { VmType } from './vcloud-vm-item';
import { NumericInput } from '@/components/numeric-input';

/**
 * Classic table presentation for one VCloud group (VM instances / load balancers
 * / other services). Chosen over the card-per-row layout because the console's
 * users are sales / customer-care staff who read spreadsheet-style tables far
 * more comfortably: one header row of column labels, one line per item, an
 * explicit line-total column, and a subtotal footer — like a quotation table.
 * Writes into the same flat `service_configoptions` array (items tagged by
 * `title`), so the payload is identical to the card layout.
 */

interface ConfigOption { name: string; title?: string; price?: number }

const GROUP_LABEL: Record<VmType, string> = {
  'vm-instance': 'VM instances',
  'vm-lb': 'Load balancers',
  'vm-other': 'Other services',
};

const vnd = (n: number) => formatMoney(n || 0);

function filterOptions(opts: ConfigOption[], type: VmType): ConfigOption[] {
  return opts.filter((o) => {
    const t = (o.title ?? '').toLowerCase();
    if (type === 'vm-instance') return t.includes('vm-instance');
    if (type === 'vm-lb') return t.includes('vm-lb');
    return !t.includes('vm-instance') && !t.includes('vm-lb');
  });
}

/** Bare (label-less) number input for a table cell. */
function NumCell({ path, suffix, min = 0 }: { path: string; suffix?: string; min?: number }) {
  const { control } = useFormContext<ContractFormValues>();
  return (
    <Controller
      control={control}
      name={path as never}
      render={({ field }) => (
        <NumericInput
          min={min}
          endSlot={suffix ? <span style={{ fontSize: 'var(--vn-font-size-xs)' }}>{suffix}</span> : undefined}
          value={field.value == null ? '' : String(field.value)}
          onChange={(e) => field.onChange(e.target.value === '' ? undefined : Number(e.target.value))}
          onBlur={field.onBlur}
          name={field.name}
          ref={field.ref}
        />
      )}
    />
  );
}

/** Column template + header labels for a group in the given mode. */
function columnsFor(type: VmType, custom: boolean) {
  if (type === 'vm-instance' && custom) {
    return {
      grid: 'minmax(110px,1fr) minmax(110px,1fr) 150px 150px 40px',
      heads: ['vCPU', 'RAM', 'Unit price', 'Line total', ''],
    };
  }
  return {
    grid: 'minmax(180px,2fr) 110px 150px 150px 40px',
    heads: ['Package', type === 'vm-other' ? 'Commitment' : 'Quantity', 'Unit price', 'Line total', ''],
  };
}

/** One data row. Renders bare cells; derives custom `name` from vcpu/ram. */
function Row({
  basePath, flatIndex, type, custom, configOptions, onDelete,
}: {
  basePath: string; flatIndex: number; type: VmType; custom: boolean;
  configOptions: ConfigOption[]; onDelete?: () => void;
}) {
  const { control, setValue } = useFormContext<ContractFormValues>();
  const itemPath = `${basePath}.${flatIndex}`;
  const qty = Number(useWatch({ control, name: `${itemPath}.commitment_value` as never }) ?? 0);
  const unit = Number(useWatch({ control, name: `${itemPath}.commitment_price_per_unit` as never }) ?? 0);
  const vcpu = useWatch({ control, name: `${itemPath}.vcpu` as never }) as unknown as number | undefined;
  const ram = useWatch({ control, name: `${itemPath}.ram` as never }) as unknown as number | undefined;
  const { grid } = columnsFor(type, custom);

  // Custom VMs carry a synthetic package name so the backend gets a stable id.
  useEffect(() => {
    if (!custom) return;
    setValue(`${itemPath}.name` as never, `vm-custom-${Number(vcpu ?? 0)}-${Number(ram ?? 0)}` as never, { shouldDirty: true });
  }, [custom, vcpu, ram, itemPath, setValue]);

  const filtered = filterOptions(configOptions, type);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: grid, gap: 'var(--vn-space-2)', alignItems: 'center', padding: 'var(--vn-space-2) 0', borderTop: '1px solid var(--vn-border-default)' }}>
      {custom ? (
        <>
          <NumCell path={`${itemPath}.vcpu`} suffix="cores" />
          <NumCell path={`${itemPath}.ram`} suffix="GB" />
        </>
      ) : (
        <>
          <Controller
            control={control}
            name={`${itemPath}.name` as never}
            render={({ field }) => (
              <Select value={(field.value as string) || ''} onValueChange={field.onChange}>
                <SelectTrigger><SelectValue placeholder="Select package" /></SelectTrigger>
                <SelectContent>
                  {(filtered.length ? filtered : [{ name: '' }]).map((o) => (
                    <SelectItem key={o.name || 'none'} value={o.name}>{o.name || 'No options'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          <NumCell path={`${itemPath}.commitment_value`} suffix="unit" min={0} />
        </>
      )}
      <NumCell path={`${itemPath}.commitment_price_per_unit`} />
      <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 'var(--vn-font-size-sm)' }}>
        {vnd((custom ? 1 : qty) * unit)}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        {onDelete && (
          <Button type="button" variant="ghost" size="sm" onClick={onDelete} aria-label="Remove row">
            <Trash2 size={14} />
          </Button>
        )}
      </div>
    </div>
  );
}

export function VCloudClassicTable({
  optsPath, type, packageMode, configOptions,
}: {
  optsPath: string; type: VmType; packageMode: 'predefined' | 'custom'; configOptions: ConfigOption[];
}) {
  const { getValues, setValue } = useFormContext<ContractFormValues>();
  const all = (useWatch({ name: optsPath as never }) ?? []) as (ContractServiceConfigOption & { title?: string })[];
  const custom = type === 'vm-instance' && packageMode === 'custom';

  const ownIndices = all.reduce<number[]>((acc, item, i) => {
    if ((item as { title?: string }).title === type) acc.push(i);
    return acc;
  }, []);

  const add = () => {
    const current = (getValues(optsPath as never) as unknown as ContractServiceConfigOption[]) ?? [];
    const seed: ContractServiceConfigOption & { title: string } = {
      name: '', title: type, type, unit: 'package',
      commitment_value: 1, commitment_price_per_unit: 0, commitment_time: 1,
      commitment_prepaid: true, statementable: false,
      ...(custom ? { vcpu: 0, ram: 0 } : {}),
    };
    setValue(optsPath as never, [...current, seed] as never, { shouldDirty: true });
  };

  const remove = (flatIndex: number) => {
    const current = (getValues(optsPath as never) as unknown as ContractServiceConfigOption[]) ?? [];
    setValue(optsPath as never, current.filter((_, i) => i !== flatIndex) as never, { shouldDirty: true });
  };

  const canAdd = !(custom && ownIndices.length >= 1);
  const { grid, heads } = columnsFor(type, custom);

  return (
    <div>
      {/* Group title row — a plain header inside the parent panel (no nested box). */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 'var(--vn-space-1)' }}>
        <strong style={{ fontSize: 'var(--vn-font-size-sm)' }}>{GROUP_LABEL[type]} ({ownIndices.length})</strong>
        {canAdd && (
          <Button type="button" variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={add}>Add</Button>
        )}
      </div>

      <div>
        {ownIndices.length === 0 ? (
          <EmptyState title={`No ${GROUP_LABEL[type].toLowerCase()} yet`} />
        ) : (
          <>
            {/* Header row */}
            <div style={{ display: 'grid', gridTemplateColumns: grid, gap: 'var(--vn-space-2)', padding: 'var(--vn-space-2) 0', fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', fontWeight: 600 }}>
              {heads.map((h, i) => (
                <span key={i} style={{ textAlign: i === 3 ? 'right' : 'left' }}>{h}</span>
              ))}
            </div>
            {ownIndices.map((flatIndex) => (
              <Row
                key={flatIndex}
                basePath={optsPath}
                flatIndex={flatIndex}
                type={type}
                custom={custom}
                configOptions={configOptions}
                onDelete={ownIndices.length > 1 || type !== 'vm-instance' ? () => remove(flatIndex) : undefined}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}
